<template>
	<label
		:class="[
			'el-input', valid === false ? '-invalid' : null,
			valid === true ? '-valid' : null
		]"
	>
		<div class="input-label">
			{{ label }}
		</div>

		<div class="input-container -text-form-input">
			<div class="input-box">
				<input ref="input"
					v-model="currentValue"
					:placeholder="!usePlaceholderAsMask ? placeholder : null"
					:type="type"
					:maxlength="maxlength"
					:pattern="pattern"
					required
					@keypress="keypress"
				>

				<div class="checkmark">
					<ElCheckIcon v-if="valid === true" isValid />
					<ElCheckIcon v-if="valid === false" />
				</div>

				<div v-if="usePlaceholderAsMask" class="input-mask">
					<span class="current-value">{{ currentValue }}</span>
					<span class="remaining-value">{{ remainingValue }}</span>
				</div>
			</div>
		</div>
	</label>
</template>

<script>
	import ElCheckIcon from '../components/ElCheckIcon';
	import {
		strictFormat
	} from '../utils/input';


	export default {
		name: 'ElInput',
		components: {
			ElCheckIcon
		},
		props: {
			keypress: {
				type: Function,
				description: 'inputed pattern',
				default: () => null
			},
			label: {
				type: String,
				description: 'Text to be used as the input\'s label.',
				default: null
			},
			placeholder: {
				type: String,
				description: 'Text to be displayed when the input is unfilled.',
				default: null
			},
			maxlength: {
				type: Number,
				description: 'Max length for input.',
				default: 150
			},
			type: {
				type: String,
				description: 'Text to be displayed when the input is unfilled.',
				default: null
			},
			pattern: {
				type: String,
				description: 'inputed pattern',
				default: null
			},
			value: {
				default: null
			},
			validator: {
				type: Function,
				default: null
			},
			autoformat: {
				type: String,
				default: null
			},
			filter: {
				type: Function,
				default: null
			},
			usePlaceholderAsMask: {
				type: Boolean
			},
			required: {
				type: Boolean
			}
		},
		data() {
			return {
				currentValue: '',
				valid: true
			}
		},
		computed: {
			remainingValue() {
				return this.placeholder.substr((this.currentValue || '').length);
			}
		},
		watch: {
			currentValue(newValue, oldValue) {
				if (newValue === null) {
					return
				}
				if (typeof this.filter === 'function') {
					const result = this.filter(newValue, newValue.length - 1);
					if (result === false) {
						this.currentValue = newValue = oldValue;
					}
				}
				if (typeof this.autoformat === 'string' && this.autoformat.length > 0) {
					this.currentValue = strictFormat(newValue, this.autoformat);
				}
				this.$emit('input', newValue);
				this.validate();
			},
			value(newValue) {
				this.currentValue = newValue;
			}
		},
		mounted() {
			this.currentValue = this.value;
		},
		methods: {
			validate() {
				if (typeof this.validator === 'function') {
					this.valid = this.validator(this.currentValue);
				} else {
					this.valid = true;
				}
				this.$emit('validated', this.valid);
			}
		}
	}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
	@import "../assets/sass/variables";

	.el-input {
		display: block;
		width: 100%;

		>.input-label {
			display: block;
			margin-bottom: 1rem;

			text-align: left;
			color: $color-text-light;
			font-size: 1.6rem;
			font-weight: bold;
		}

		>.input-container {
			position: relative;

			>.input-box {
				display: flex;
				align-items: center;
				justify-content: space-between;

				background: $color-white;
				border: 1px solid $color-light;
				border-radius: $border-radius;

				cursor: text;

				font-family: 'Azo Sans';
				padding: 1.6rem;
				text-align: left;
				transition: box-shadow .3s linear;

				&:focus-within {
					box-shadow: $box-shadow;
				}

				input,
				textarea {
					line-height: 1;
					color: $color-text;
					font-family: inherit;
					font-weight: 500;
					font-size: 1.8rem;
					background: transparent;
					border: none;
					color: inherit;
					cursor: inherit;
					display: block;
					outline: none;
					resize: vertical;
					padding: 0;
					width: 100%;

					// Blinking text cursor
					// https://css-tricks.com/almanac/properties/c/caret-color/
					caret-color: $color-text;
				}

				.input-mask {
					flex: none;
					position: absolute;

					>span {
						line-height: 1;
						color: #000;
						font-family: inherit;
						font-weight: 500;
						font-size: 2rem;
						color: inherit;
						padding: 0;
						width: 100%;
						opacity: 0.8;
					}

					.current-value {
						opacity: 0;
						font-size: 1.8rem;
					}
				}

				.checkmark {
					position: absolute;
					right: 5px;
					top: 50%;
					transform: translateY(-50%) scale(.8);
				}
			}

			&.-invalid>.input-container>.input-box {
				border: 1px solid $color-accent-invalid;
			}
		}
	}
</style>